'use client';

import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

export type SortOrder = 'asc' | 'desc' | false;

type SortableItem = {
  name?: string;
  campaignName?: string;
  impressions?: string;
  clicks?: string;
  ctr?: string;
  cpm?: string;
  cpc?: string;
};

export function useBannerSort<T extends SortableItem>(
  banners: T[] | undefined
) {
  const [isSorting, setIsSorting] = useState(false);
  const [sortByOrderName, setSortByOrderName] = useState<SortOrder>(false);
  const [sortByOrderImpressions, setSortByOrderImpressions] =
    useState<SortOrder>(false);
  const [sortByOrderClicks, setSortByOrderClicks] = useState<SortOrder>(false);
  const [sortByOrderCTR, setSortByOrderCTR] = useState<SortOrder>(false);
  const [sortByOrderCPM, setSortByOrderCPM] = useState<SortOrder>(false);
  const [sortByOrderCPC, setSortByOrderCPC] = useState<SortOrder>(false);

  const [debouncedSortByOrderName, setDebouncedSortByOrderName] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderImpressions, setDebouncedSortByOrderImpressions] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderClicks, setDebouncedSortByOrderClicks] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderCTR, setDebouncedSortByOrderCTR] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderCPM, setDebouncedSortByOrderCPM] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderCPC, setDebouncedSortByOrderCPC] =
    useState<SortOrder>(false);

  useDebounce(
    () => {
      setDebouncedSortByOrderName(sortByOrderName);
    },
    1,
    [sortByOrderName]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderImpressions(sortByOrderImpressions);
    },
    1,
    [sortByOrderImpressions]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderClicks(sortByOrderClicks);
    },
    1,
    [sortByOrderClicks]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderCTR(sortByOrderCTR);
    },
    1,
    [sortByOrderCTR]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderCPM(sortByOrderCPM);
    },
    1,
    [sortByOrderCPM]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderCPC(sortByOrderCPC);
    },
    1,
    [sortByOrderCPC]
  );

  const resetSortStates = (ignoreSet: (value: SortOrder) => void) => {
    const setters = {
      setSortByOrderName,
      setSortByOrderImpressions,
      setSortByOrderClicks,
      setSortByOrderCTR,
      setSortByOrderCPM,
      setSortByOrderCPC,
    };

    Object.values(setters).forEach(setter => {
      if (setter !== ignoreSet) {
        setter(false);
      }
    });
  };

  const toggleSort = (
    current: SortOrder,
    setSort: (value: SortOrder) => void
  ) => {
    setIsSorting(true);
    setTimeout(() => {
      resetSortStates(setSort);
      switch (current) {
        case 'asc':
          setSort('desc');
          break;
        case 'desc':
          setSort(false);
          break;
        default:
          setSort('asc');
          break;
      }
      setIsSorting(false);
    }, 300);
  };

  const sortedBanners = useMemo(() => {
    const bannersList = banners ?? [];

    if (debouncedSortByOrderName) {
      return [...bannersList].sort((a, b) => {
        const nameA = (a.name ?? a.campaignName ?? '').toLowerCase();
        const nameB = (b.name ?? b.campaignName ?? '').toLowerCase();
        return debouncedSortByOrderName === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
    }

    if (debouncedSortByOrderImpressions) {
      return [...bannersList].sort((a, b) => {
        const impressionsA = parseFloat(
          a.impressions?.replace(/\./g, '') ?? '0'
        );
        const impressionsB = parseFloat(
          b.impressions?.replace(/\./g, '') ?? '0'
        );
        return debouncedSortByOrderImpressions === 'asc'
          ? impressionsA - impressionsB
          : impressionsB - impressionsA;
      });
    }

    if (debouncedSortByOrderClicks) {
      return [...bannersList].sort((a, b) => {
        const clicksA = parseFloat(a.clicks?.replace(/\./g, '') ?? '0');
        const clicksB = parseFloat(b.clicks?.replace(/\./g, '') ?? '0');
        return debouncedSortByOrderClicks === 'asc'
          ? clicksA - clicksB
          : clicksB - clicksA;
      });
    }

    if (debouncedSortByOrderCTR) {
      return [...bannersList].sort((a, b) => {
        const ctrA = parseFloat(
          a.ctr?.replace('%', '').replace(',', '.') ?? '0'
        );
        const ctrB = parseFloat(
          b.ctr?.replace('%', '').replace(',', '.') ?? '0'
        );
        return debouncedSortByOrderCTR === 'asc' ? ctrA - ctrB : ctrB - ctrA;
      });
    }

    if (debouncedSortByOrderCPM) {
      return [...bannersList].sort((a, b) => {
        const cpmA = parseFloat(
          a.cpm?.replace('R$', '').replace('.', '').replace(',', '.').trim() ??
            '0'
        );
        const cpmB = parseFloat(
          b.cpm?.replace('R$', '').replace('.', '').replace(',', '.').trim() ??
            '0'
        );
        return debouncedSortByOrderCPM === 'asc' ? cpmA - cpmB : cpmB - cpmA;
      });
    }

    if (debouncedSortByOrderCPC) {
      return [...bannersList].sort((a, b) => {
        const cpcA = parseFloat(
          a.cpc?.replace('R$', '').replace('.', '').replace(',', '.').trim() ??
            '0'
        );
        const cpcB = parseFloat(
          b.cpc?.replace('R$', '').replace('.', '').replace(',', '.').trim() ??
            '0'
        );
        return debouncedSortByOrderCPC === 'asc' ? cpcA - cpcB : cpcB - cpcA;
      });
    }

    return bannersList;
  }, [
    banners,
    debouncedSortByOrderName,
    debouncedSortByOrderImpressions,
    debouncedSortByOrderClicks,
    debouncedSortByOrderCTR,
    debouncedSortByOrderCPM,
    debouncedSortByOrderCPC,
  ]);

  return {
    sortedBanners,
    isSorting,
    sortByOrderName,
    sortByOrderImpressions,
    sortByOrderClicks,
    sortByOrderCTR,
    sortByOrderCPM,
    sortByOrderCPC,
    setSortByOrderName,
    setSortByOrderImpressions,
    setSortByOrderClicks,
    setSortByOrderCTR,
    setSortByOrderCPM,
    setSortByOrderCPC,
    toggleSort,
  };
}
